<app-logo-area
  [breadcrumbs]="[
    {'display': 'Content Platform', commands: null},
    {'display': 'Signiant Connectors', commands: ['/o', organization()?.id, 'signiant-portals']}]"></app-logo-area>
<main>

  <app-headline-section [title]="portalName()">
  </app-headline-section>

  @if (error()) {
    <section class="cl-section cl-section-regular">
      <div class="container"><div class="row"><div class="col-12">
        <p class="cl-p-regular cl-color-brick">Could not load this portal: {{ error() }}</p>
      </div></div></div>
    </section>
  }

  <!-- ============================================================ session -->
  <!-- Full width: the session belongs to the portal, not to a direction, and
       both halves below are useless without it. -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12">
          <h3 class="cl-h3">Session</h3>
          <table style="table-layout: fixed;" class="cl-table-data">
            <colgroup><col style="width: 40%"><col style="width: 60%"></colgroup>
            <tbody>
              <tr>
                <td>Status</td>
                <td>
                  @if (portal()?.status?.loggedIn) {
                    <span class="cl-color-sea">Logged in</span>
                  } @else if (portal()?.status?.needsMfa) {
                    <span class="cl-color-sun">MFA required — an operator must log in</span>
                  } @else {
                    <span class="cl-color-brick">Signed out</span>
                  }
                </td>
              </tr>
              <tr><td>Account</td><td>{{ portal()?.status?.username || '—' }}</td></tr>
              <tr><td>Portal</td><td>{{ portal()?.portalUrl }}</td></tr>
              <tr>
                <td>Last swept</td>
                <td class="cl-td-date"><span class="cl-font-monospace">{{ portal()?.lastSweepAt ? (portal()!.lastSweepAt | cldate) : '—' }}</span></td>
              </tr>
            </tbody>
          </table>

          @if ('contentplatform:signiant:Mutation/Login' | isAllowedAnywhere) {
            <!-- The session expires after a while and reviving it is a human
                 act — for an MFA portal the second factor is mailed. One line:
                 the button holds the right edge, the fields fill the rest. -->
            <div class="login-box cl-form">
              <div class="login-row">
                @if (needsCredentials()) {
                  <!-- one-time per portal: the API stores this in the portal's secret -->
                  <input type="text" autocomplete="username" placeholder="username"
                         [(ngModel)]="username">
                  <input type="password" autocomplete="current-password" placeholder="password"
                         [(ngModel)]="password" (keyup.enter)="login(loginButton)">
                }
                @if (mfaPending()) {
                  <input type="text" class="cl-field-monospace" autocomplete="one-time-code"
                         placeholder="code from the e-mail" [(ngModel)]="mfaCode"
                         (keyup.enter)="submitMfa(mfaButton)">
                  <app-do-button #mfaButton [messageField]="loginMessage"
                                 (actionTriggered)="submitMfa($event)">Confirm</app-do-button>
                } @else {
                  <app-do-button #loginButton [messageField]="loginMessage"
                                 (actionTriggered)="login($event)">
                    {{ portal()?.status?.loggedIn ? 'Log in again' : 'Log in' }}
                  </app-do-button>
                }
              </div>
              <app-message-field #loginMessage></app-message-field>
            </div>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- ================================================= download | upload -->
  <!-- One half per direction, always both columns. A portal that does not do one
       of them leaves that half empty rather than letting the other stretch
       across — the layout then says at a glance what this portal is for. -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">

        <div class="col-12 col-lg-6">
          @if (!direction()) {
            <h3 class="cl-h3">Download</h3>
            <table style="table-layout: fixed;" class="cl-table-data">
              <tbody><tr class="cl-tr-no-data">
                <td colspan="2">Loading ...<i class="fas fa-spinner fa-spin"></i></td>
              </tr></tbody>
            </table>
          } @else if (hasDownload()) {
            <h3 class="cl-h3">Download</h3>
            <p class="cl-p-tiny">What this portal hands us, and where it lands.</p>
            <table style="table-layout: fixed;" class="cl-table-data">
              <colgroup><col style="width: 40%"><col style="width: 60%"></colgroup>
              <tbody>
                <tr><td>Import folder</td>
                  <td>{{ downloadConfig()?.portalFolder || 'the whole portal' }}</td></tr>
                <tr><td>Drop destination</td>
                  <td>{{ downloadConfig()?.dropDestination || '—' }}</td></tr>
                <tr>
                  <td>Downloads</td>
                  <td>{{ autoDownloadPaused() ? 'Nothing, the mark is in the future' : 'Automatic' }}
                    @if (downloadConfig()?.pollIntervalMinutes) {
                      <small class="text-muted">— swept every {{ downloadConfig()!.pollIntervalMinutes }} min</small>
                    }
                  </td>
                </tr>
                <tr>
                  <!-- The dedup position: only files modified after this are
                       picked up. A date in the future therefore picks up
                       nothing — a consequence of it being a position, not a
                       switch anyone designed. -->
                  <td>Downloading from</td>
                  <td class="cl-td-date"><span class="cl-font-monospace">{{ autoDownloadWatermark() ? (autoDownloadWatermark() | cldate) : '—' }}</span></td>
                </tr>
              </tbody>
            </table>
          }
        </div>

        <div class="col-12 col-lg-6">
          @if (!direction()) {
            <h3 class="cl-h3">Upload</h3>
            <table style="table-layout: fixed;" class="cl-table-data">
              <tbody><tr class="cl-tr-no-data">
                <td colspan="2">Loading ...<i class="fas fa-spinner fa-spin"></i></td>
              </tr></tbody>
            </table>
          } @else if (hasUpload()) {
            <h3 class="cl-h3">Upload</h3>
            <p class="cl-p-tiny">What we send to this portal, and where it comes from.</p>
            <table style="table-layout: fixed;" class="cl-table-data">
              <colgroup><col style="width: 40%"><col style="width: 60%"></colgroup>
              <tbody>
                <tr><td>Source bucket</td><td>{{ uploadConfig()?.sourceBucket || '—' }}</td></tr>
                <tr><td>Source prefix</td><td>{{ uploadConfig()?.sourcePrefix || '—' }}</td></tr>
                <tr>
                  <td>Uploads</td>
                  <td>Automatic
                    @if (uploadConfig()?.pollIntervalMinutes) {
                      <small class="text-muted">— swept every {{ uploadConfig()!.pollIntervalMinutes }} min</small>
                    }
                  </td>
                </tr>
              </tbody>
            </table>
          }
        </div>

      </div>
    </div>
  </section>

  <!-- ============================================================ imports -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        @if (hasDownload()) {
        <h3 class="cl-h3">Recent Downloads</h3>
        <p class="cl-p-tiny">The 30 most recent transfers of this portal, both
          directions together, filtered to downloads; older ones stay in the
          process store.</p>
        <table style="table-layout: fixed;" class="cl-table-data">
          <colgroup>
            <col style="width: 40%">
            <col style="width: 150px">
            <col style="width: 100%">
            <col style="width: 160px">
          </colgroup>
          <thead>
            <tr>
              <th class="cl-table-head cl-p-tiny">File</th>
              <th class="cl-table-head cl-p-tiny">State</th>
              <th class="cl-table-head cl-p-tiny">Message</th>
              <th class="cl-table-head cl-p-tiny">Started</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr class="cl-tr-no-data">
                <td colspan="100" style="text-align: center">
                    Loading ...<i class="fas fa-spinner fa-spin"></i></td>
              </tr>
            }
            @for (process of downloads(); track process.id) {
              <tr>
                <td>{{ process.fileName }}</td>
                <td>
                  @if (process.state === 'SUCCESS') {
                    <span class="cl-badge cl-badge-sm cl-color-leaf">Success</span>
                  } @else if (process.state === 'FAILED') {
                    <span class="cl-badge cl-badge-sm cl-color-brick">Failed</span>
                  } @else {
                    <span class="cl-badge cl-badge-sm cl-color-honey">In progress {{ process.percent }}%</span>
                  }
                </td>
                <td>{{ process.message || '—' }}</td>
                <td class="cl-td-date"><span class="cl-font-monospace">{{ process.start_date ? (process.start_date | cldate) : '—' }}</span></td>
              </tr>
            }
            @if (!loading() && downloads().length === 0) {
              <tr><td colspan="4">Nothing has been downloaded from this portal yet.</td></tr>
            }
          </tbody>
        </table>
        }
        @if (hasUpload()) {
        <h3 class="cl-h3">Recent Uploads</h3>
        <p class="cl-p-tiny">The 30 most recent transfers of this portal, both
          directions together, filtered to uploads; older ones stay in the
          process store.</p>
        <table style="table-layout: fixed;" class="cl-table-data">
          <colgroup>
            <col style="width: 40%">
            <col style="width: 150px">
            <col style="width: 100%">
            <col style="width: 160px">
          </colgroup>
          <thead>
            <tr>
              <th class="cl-table-head cl-p-tiny">File</th>
              <th class="cl-table-head cl-p-tiny">State</th>
              <th class="cl-table-head cl-p-tiny">Message</th>
              <th class="cl-table-head cl-p-tiny">Started</th>
            </tr>
          </thead>
          <tbody>
            @if (loading()) {
              <tr class="cl-tr-no-data">
                <td colspan="100" style="text-align: center">
                    Loading ...<i class="fas fa-spinner fa-spin"></i></td>
              </tr>
            }
            @for (process of uploads(); track process.id) {
              <tr>
                <td>{{ process.fileName }}</td>
                <td>
                  @if (process.state === 'SUCCESS') {
                    <span class="cl-badge cl-badge-sm cl-color-leaf">Success</span>
                  } @else if (process.state === 'FAILED') {
                    <span class="cl-badge cl-badge-sm cl-color-brick">Failed</span>
                  } @else {
                    <span class="cl-badge cl-badge-sm cl-color-honey">In progress {{ process.percent }}%</span>
                  }
                </td>
                <td>{{ process.message || '—' }}</td>
                <td class="cl-td-date"><span class="cl-font-monospace">{{ process.start_date ? (process.start_date | cldate) : '—' }}</span></td>
              </tr>
            }
            @if (!loading() && uploads().length === 0) {
              <tr><td colspan="4">Nothing has been uploaded to this portal yet.</td></tr>
            }
          </tbody>
        </table>
        }
      </div></div>
    </div>
  </section>

  <!-- ============================================================ work list -->
  <!-- One direction's work list or the other's: what an RX portal is offering
       us, or what the publish bucket is holding for a TX portal. -->
  <!-- ================================================== files on the portal -->
  <!-- The same browser for both portal types: an acquisition portal is browsed
       to pick imports from, a delivery portal to confirm what we sent arrived.
       Only the per-row action differs, and it is projected in below. -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Files on the portal</h3>
        <!-- Nothing here until the direction is known. Not only to avoid showing
             an Import button on a delivery portal for a moment: the browser
             fetches the portal root when it mounts, and rendering one variant and
             then swapping it for another would mount it twice and list the root
             twice. -->
        @if (!direction()) {
          <table style="table-layout: fixed;" class="cl-table-data">
            <tbody>
              <tr class="cl-tr-no-data">
                <td colspan="100" style="text-align: center">
                  Loading ...<i class="fas fa-spinner fa-spin"></i></td>
              </tr>
            </tbody>
          </table>
        } @else {
        <p class="cl-p-tiny">
          @if (hasUpload() && hasDownload()) {
            What is on the portal now — both what we delivered and what is waiting
            for us. Browse a directory at a time.
          } @else if (hasUpload()) {
            What is on the portal now — this is where a delivery lands, so it is
            how you confirm one arrived.
          } @else {
            Browse a directory at a time and download what you need.
          }
        </p>
        @if (hasDownload() && ('contentplatform:signiant:Mutation/Import' | isAllowedAnywhere: organization())) {
          <app-portal-browser [portalId]="portalId" actionHeader="Import">
            <ng-template let-entry>
              @if (importState(entry) === 'running') {
                <span class="cl-badge cl-badge-sm cl-color-honey">In progress {{ processFor(entry)?.percent }}%</span>
              } @else if (importState(entry) === 'done') {
                <span class="cl-badge cl-badge-sm cl-color-leaf">Success</span>
              } @else if (importState(entry) === 'failed') {
                <span class="cl-badge cl-badge-sm cl-color-brick">Failed</span>
              }
              <!-- Always rendered, whatever the state: while a process runs the
                   directive spins it and swallows the click, so there is no
                   second trigger to guard against -- and an import started by
                   the scheduled sweep spins it just the same. After a success it
                   stays, because a file that went missing downstream has to be
                   re-importable. -->
              <a class="cl-a-btn cl-a-primary"
                 [processButton]="importButtonId(entry)"
                 (click)="startImport(entry)">{{ importState(entry) === 'failed' ? 'Retry' : 'Download' }}</a>
            </ng-template>
          </app-portal-browser>
        } @else {
          <app-portal-browser [portalId]="portalId" />
        }
        }
      </div></div>
    </div>
  </section>
</main>